/**
 * 案件の一覧（039、docs/28 §4）。/groups。サイドメニューの「案件」
 *   ・行：案件名（顧客名・支店。敬称は付けない）・担当者・見積の枚数・受注（税込。下に小さく税抜＝#94）・請求済み・状態（見積から自動で決まる）・動いた日
 *   ・絞り込み：進行中／完了／すべて のタブ（失注だけの案件は「完了」の側。9/24 にボタンからタブへ、幅はそろえる）・担当者・名前と顧客で探す（タブの右）
 *   ・右上「新しい案件」。行を押すと案件の画面
 */
import { GROUP_MSG, GROUP_STATUS_LABEL, ONGOING_MSG, type GroupCreate, type GroupDetail, type GroupList, type GroupListItem, type GroupListState } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../api.js";
import { AmtCell } from "../components/AmtCell.jsx";
import { FilterInput, ListHead } from "../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../components/ListTabs.jsx";
import { usePageName } from "../components/BackButton.jsx";
import { Icon } from "../components/Shell.jsx";
import { ErrorText, useToast, type ToastAction } from "../components/ui.jsx";
import type { NoticeTone } from "../components/Notice.jsx";
import { RowMenu, type RowMenuItem } from "../components/RowMenu.jsx";
import { t, useLabels } from "../labels.js";
import { useOngoing } from "../lib/ongoing.js";
import { md, yen } from "../lib/num.js";

export function StatusChip({ status }: { status: GroupListItem["status"] }) {
  return <span className={"chip" + (status === "ordered" ? " ai" : "")}>{GROUP_STATUS_LABEL[status]}</span>;
}

/**
 * 前の画面から渡された一言（navigate の state.notice）のうち、state.recreate のあるもの（案件を消した直後）を「元に戻す」付きの通知で出す
 * ＝同じ中身の案件を作り直す。1回出したら state を消す（読み込み直しで2回出さない）。recreate の無い一言は App の NoticeHost が出す（#168）
 */
export function useFlash(toast: (text: string | null, action?: ToastAction, tone?: NoticeTone) => void) {
  const loc = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const done = useRef(false);
  useEffect(() => {
    const st = loc.state as { notice?: string; recreate?: GroupCreate; fresh?: boolean } | null;
    if (done.current || !st?.notice || !st.recreate) return;
    done.current = true;
    const again = st.recreate;
    toast(st.notice, {
      label: "元に戻す",
      onClick: () =>
        void api<GroupDetail>("POST", "/groups", again)
          .then((g) => {
            void qc.invalidateQueries({ queryKey: ["group-list"] });
            nav(`/groups/${g.id}`);
          })
          .catch((e: unknown) => toast(messageOf(e), undefined, "error")),
    });
    const { notice: _n, recreate: _r, ...rest } = st;
    nav(loc.pathname + loc.search, { replace: true, state: Object.keys(rest).length ? rest : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function GroupsPage() {
  const L = useLabels();
  const og = useOngoing();
  usePageName(L.project);
  // #289（docs/101 §7、Figma ④）：タブ 進行中（既定）／継続（使う機能「{ongoingProject}」がオンの会社だけ）／完了／すべて
  const TABS: [GroupListState, string][] = [["active", ONGOING_MSG.tabActive], ...(og.on ? ([["ongoing", og.short]] as [GroupListState, string][]) : []), ["done", ONGOING_MSG.tabDone], ["all", ONGOING_MSG.tabAll]];
  const qc = useQueryClient();
  const [rename, setRename] = useState<{ g: GroupListItem; name: string; busy: boolean; error?: string } | null>(null);
  const [del, setDel] = useState<{ g: GroupListItem; detail: GroupDetail | null; busy: boolean } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["group-list"] });
  const nav = useNavigate();
  const { toast, node: toastNode } = useToast();
  useFlash(toast);
  const [sp, setSp] = useSearchParams();
  const state0 = (sp.get("state") as GroupListState | null) ?? "active";
  const state: GroupListState = state0 === "ongoing" && !og.on ? "active" : state0;
  const staff = sp.get("staff") ?? "";
  const [q, setQ] = useState(sp.get("q") ?? "");
  const [qApplied, setQApplied] = useState(q);
  // 打つたびに引かない（0.3秒おいてから）
  useEffect(() => {
    const t = setTimeout(() => setQApplied(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const params = new URLSearchParams({ state, ...(staff ? { staff } : {}), ...(qApplied ? { q: qApplied } : {}) });
  const list = useQuery({ queryKey: ["group-list", state, staff, qApplied], queryFn: () => api<GroupList>("GET", `/groups?${params.toString()}`) });
  const items = list.data?.items ?? [];
  // 進行中（とすべて）の中は 継続案件を先頭に「{ongoingShort}」の区切り、続けて「{pw:regularGroups}」の区切り（継続案件がある会社だけ）
  const sectioned = og.on && (state === "active" || state === "all") && items.some((g) => g.isOngoing);
  const ongoingRows = sectioned ? items.filter((g) => g.isOngoing) : [];
  const regularRows = sectioned ? items.filter((g) => !g.isOngoing) : items;
  const act = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      refresh();
      toast(done);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const menuOf = (g: GroupListItem): RowMenuItem[] => [
    { label: ONGOING_MSG.rename, onSelect: () => setRename({ g, name: g.name, busy: false }), testId: "group-row-rename" },
    ...(og.on
      ? [{
          label: ONGOING_MSG.toggleOngoing,
          onSelect: () => void act(() => api("PATCH", `/groups/${g.id}`, { isOngoing: !g.isOngoing }), t(g.isOngoing ? ONGOING_MSG.toRegular : ONGOING_MSG.toOngoing).replace("{name}", g.name)),
          testId: "group-row-ongoing",
          checked: g.isOngoing,
        } satisfies RowMenuItem]
      : []),
    g.completedAt
      ? { label: ONGOING_MSG.reopen, onSelect: () => void act(() => api("POST", `/groups/${g.id}/reopen`, {}), t(GROUP_MSG.reopened).replace("{name}", g.name)), testId: "group-row-reopen" }
      : { label: ONGOING_MSG.complete, onSelect: () => void act(() => api("POST", `/groups/${g.id}/complete`, {}), t(GROUP_MSG.completed).replace("{name}", g.name)), testId: "group-row-complete" },
    {
      label: ONGOING_MSG.remove,
      danger: true,
      divider: true,
      testId: "group-row-delete",
      onSelect: () => {
        setDel({ g, detail: null, busy: true });
        void api<GroupDetail>("GET", `/groups/${g.id}`)
          .then((d) => setDel({ g, detail: d, busy: false }))
          .catch((e: unknown) => {
            setDel(null);
            toast(messageOf(e), undefined, "error");
          });
      },
    },
  ];
  const saveName = async () => {
    if (!rename) return;
    const name = rename.name.trim();
    if (!name) return setRename({ ...rename, error: t(GROUP_MSG.nameRequired) });
    const { error: _e, ...r } = rename;
    setRename({ ...r, busy: true });
    try {
      await api("PUT", `/groups/${rename.g.id}`, { name });
      setRename(null);
      refresh();
      toast(t(ONGOING_MSG.renamed).replace("{name}", name));
    } catch (e) {
      setRename({ ...rename, busy: false, error: messageOf(e) });
    }
  };
  const remove = async () => {
    if (!del) return;
    setDel({ ...del, busy: true });
    try {
      await api("DELETE", `/groups/${del.g.id}`);
      setDel(null);
      refresh();
      toast(t(GROUP_MSG.deleted).replace("{name}", del.g.name));
    } catch (e) {
      setDel(null);
      toast(messageOf(e), undefined, "error");
    }
  };
  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v);
    else next.delete(k);
    setSp(next, { replace: true });
  };

  return (
    <ListHead title={L.project}>
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      <div className="lcard">
      <ListTabs
        id="gl"
        label={`${L.project}を状態で絞る`}
        tabs={TABS.map(([k, label]) => ({ key: k, label, count: list.data?.counts[k] ?? "…" }))}
        value={state}
        onChange={(k) => setParam("state", k === "active" ? "" : k)}
        filterMin={420}
        filter={
          <span className="lfilter-row">
            <label className="hint" htmlFor="gl_staff">担当</label>
            <select id="gl_staff" className="inp" style={{ width: "auto", minWidth: "9em" }} value={staff} onChange={(e) => setParam("staff", e.target.value)}>
              <option value="">すべて</option>
              {(list.data?.staffNames ?? (staff ? [staff] : [])).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <FilterInput value={q} onChange={setQ} placeholder={`${L.project}名・${L.customer}名で絞り込み`} testId="group-search" />
          </span>
        }
        create={
          <Link className="btn primary" to="/groups/new" data-testid="group-new">
            <Icon k="plus" />
            {L.project}作成
          </Link>
        }
      />
      <div className="tbl-wrap ltab-panel" {...listTabPanel("gl", state)}>
        <table className="list" data-testid="group-list">
          <thead>
            <tr>
              <th>{L.project}名</th>
              <th>担当</th>
              <th className="amt">見積</th>
              <th className="amt">受注（税込）</th>
              <th className="amt">請求済</th>
              <th>いまの状態</th>
              <th>動いた日</th>
              <th aria-label="操作" />
            </tr>
          </thead>
          <tbody>
            {sectioned ? (
              <tr className="lsection" data-testid="group-section-ongoing">
                <td colSpan={8}>{og.short}<span className="num">{ongoingRows.length}</span></td>
              </tr>
            ) : null}
            {ongoingRows.map((g) => <GroupRow key={g.id} g={g} ongoing={og.on} short={og.short} menu={menuOf(g)} onOpen={() => nav(`/groups/${g.id}`)} />)}
            {sectioned && regularRows.length ? (
              <tr className="lsection" data-testid="group-section-regular">
                <td colSpan={8}>{t("{pw:regularGroups}")}<span className="num">{regularRows.length}</span></td>
              </tr>
            ) : null}
            {regularRows.map((g) => <GroupRow key={g.id} g={g} ongoing={og.on} short={og.short} menu={menuOf(g)} onOpen={() => nav(`/groups/${g.id}`)} />)}
            {list.data && !items.length ? (
              <tr className="emptyrows">
                <td colSpan={8}>{list.data.counts.all || staff || qApplied ? `この絞り込みの${L.project}はいまありません。` : `まだ${L.project}はありません。「${L.project}作成」か「見積書作成」から作れます。`}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      </div>
      {rename ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !rename.busy && setRename(null)}>
          <form className="dlg" role="dialog" aria-label={ONGOING_MSG.renameTitle} onKeyDown={(e) => e.key === "Escape" && !rename.busy && setRename(null)} onSubmit={(e) => { e.preventDefault(); void saveName(); }} data-testid="group-rename">
            <div className="dh">{ONGOING_MSG.renameTitle}</div>
            <div className="db">
              <input className="inp" autoFocus maxLength={200} aria-label={`${L.project}名`} value={rename.name} onChange={(e) => setRename({ ...rename, name: e.target.value })} data-testid="group-rename-input" />
              <ErrorText>{rename.error}</ErrorText>
            </div>
            <div className="df">
              <button type="button" className="btn" disabled={rename.busy} onClick={() => setRename(null)}>取消</button>
              <button type="submit" className="btn primary" disabled={rename.busy} data-testid="group-rename-save">決定</button>
            </div>
          </form>
        </div>
      ) : null}
      {del ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !del.busy && setDel(null)}>
          <div className="dlg" role="alertdialog" aria-label={t(GROUP_MSG.deleteTitle)} onKeyDown={(e) => e.key === "Escape" && !del.busy && setDel(null)} data-testid="group-row-delete-dialog">
            <div className="dh">{t(GROUP_MSG.deleteTitle)}</div>
            <div className="db">
              <p style={{ margin: 0 }}>
                {!del.detail
                  ? "…"
                  : del.detail.trash.count
                    ? t(del.detail.trash.numbers.length ? GROUP_MSG.deleteWithTrashNumbered : GROUP_MSG.deleteWithTrash).replace("{name}", del.g.name).replace("{n}", String(del.detail.trash.count)).replace("{numbers}", del.detail.trash.numbers.join("、"))
                    : t(ONGOING_MSG.deleteAsk).replace("{name}", del.g.name)}
              </p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus disabled={del.busy} onClick={() => setDel(null)}>取消</button>
              <button type="button" className="btn shu" disabled={del.busy || !del.detail} onClick={() => void remove()} data-testid="group-row-delete-confirm">削除</button>
            </div>
          </div>
        </div>
      ) : null}
      {toastNode}
    </ListHead>
  );
}

/**
 * 一覧の1行。継続案件（#289、Figma ④）は 2行目に「開始 yyyy/mm/dd」、状態は札「{ongoingShort}」、
 * 受注の欄は「今月売上」・請求済の欄は「未請求」（税抜）。行の ⋯ は 名称変更・継続切替・完了・削除
 */
function GroupRow({ g, ongoing, short, menu, onOpen }: { g: GroupListItem; ongoing: boolean; short: string; menu: RowMenuItem[]; onOpen: () => void }) {
  const asOngoing = ongoing && g.isOngoing;
  return (
    <tr className="r" tabIndex={0} onClick={onOpen} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && onOpen()} data-testid={asOngoing ? "group-row-ongoing-row" : undefined}>
      <td>
        <b>{g.name}</b>
        {/* 顧客の名前（と支店名）だけ。「御中」「様」は付けない（docs/10 §5.8、9/24 に案件の一覧も） */}
        <div className="muted">
          {g.client.name}
          {g.branchName ? ` ${g.branchName}` : ""}
          {asOngoing && g.startedOn ? <span data-testid="group-row-started">　{ONGOING_MSG.started(g.startedOn)}</span> : null}
        </div>
      </td>
      <td>{g.staffName || <span className="muted">—</span>}</td>
      <td className="amt num">{g.jobCount}枚</td>
      {asOngoing ? (
        <>
          <td className="amt num" data-testid="group-row-month-sales">
            {yen(g.monthSales)} 円<div className="muted" style={{ fontSize: 14, fontWeight: 400 }}>{ONGOING_MSG.monthSales}</div>
          </td>
          <td className="amt num" data-testid="group-row-unbilled">
            {yen(g.unbilled)} 円<div className="muted" style={{ fontSize: 14, fontWeight: 400 }}>{ONGOING_MSG.unbilled}</div>
          </td>
        </>
      ) : (
        <>
          <td className="amt num">{g.ordered.count ? <AmtCell inc={g.ordered.total} ex={g.ordered.subtotal} testId="group-row-ex" /> : <span className="muted">—</span>}</td>
          <td className="amt num">{g.billed.count ? `${yen(g.billed.total)} 円` : <span className="muted">—</span>}</td>
        </>
      )}
      <td className="statecell">
        {asOngoing ? <span className="chip ai" data-testid="group-row-ongoing-chip">{short}</span> : <StatusChip status={g.status} />}
        {!asOngoing && g.status !== "done" && g.status !== "estimating" && g.waitingCount ? <span className="muted">未受注 {g.waitingCount}枚</span> : null}
        {!asOngoing && g.status !== "done" && g.draftCount ? <span className="muted">下書き {g.draftCount}枚</span> : null}
        {g.completedAt ? <span className="muted">{md(g.completedAt.slice(0, 10))} 完了</span> : null}
      </td>
      <td className="num">{md(g.lastActivityAt.slice(0, 10))}</td>
      <td className="rowact" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <RowMenu label={`${g.name}の操作`} items={menu} testId="group-row-menu" />
      </td>
    </tr>
  );
}
